import React, { useState, useEffect, useCallback } from 'react';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { API_URL as API } from '@/utils/apiConfig';
import {
  DollarSign, Smartphone, Apple, Globe, TrendingUp, Users, RefreshCw,
  Loader2, ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight, Store
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, Legend
} from 'recharts';


const PLATFORM_CONFIG = {
  web: { label: 'Web (Stripe)', icon: Globe, color: '#6366f1', cut: '~2.9%' },
  ios: { label: 'iOS (App Store)', icon: Apple, color: '#f59e0b', cut: '15%' },
  android: { label: 'Android (Play Store)', icon: Smartphone, color: '#10b981', cut: '15%' },
};

const PIE_COLORS = ['#6366f1', '#f59e0b', '#10b981'];

const STATUS_STYLES = {
  active: { bg: '#052e16', text: '#4ade80', bgLight: '#dcfce7', textLight: '#16a34a', label: 'Active' },
  canceling: { bg: '#78350f', text: '#fbbf24', bgLight: '#fef3c7', textLight: '#92400e', label: 'Canceling' },
  expired: { bg: '#450a0a', text: '#f87171', bgLight: '#fef2f2', textLight: '#dc2626', label: 'Expired' },
};

function fmt(n) { return n !== undefined && n !== null ? `$${n.toFixed(2)}` : '$0.00'; }

export default function AdminMobileRevenuePage() {
  const { isDark, colors } = useAdminTheme();
  const [overview, setOverview] = useState(null);
  const [subs, setSubs] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState({ platform: 'all', status: 'all', page: 1 });

  const fetchAll = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [ov, tr] = await Promise.all([
        axios.get(`${API}/admin/mobile-revenue/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/mobile-revenue/trends`, { withCredentials: true }),
      ]);
      setOverview(ov.data);
      setTrends(tr.data);
    } catch { toast.error('Failed to load revenue data'); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  const fetchSubs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/mobile-revenue/subscriptions`, {
        params: filter, withCredentials: true,
      });
      setSubs(res.data);
    } catch { /* silent */ }
  }, [filter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.accent }} />
      </div>
    </AdminLayout>
  );

  const ov = overview;
  const pieData = ov ? Object.entries(ov.platforms)
    .filter(([, v]) => v.subscribers > 0)
    .map(([k, v]) => ({ name: PLATFORM_CONFIG[k]?.label || k, value: v.subscribers, color: PLATFORM_CONFIG[k]?.color || '#6366f1' })) : [];

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl" data-testid="admin-mobile-revenue-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.text }}>Mobile Revenue</h1>
            <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
              Track subscriptions and revenue across Web, iOS, and Android
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchAll(true)} disabled={refreshing}
            style={{ borderColor: colors.cardBorder, color: colors.text }}
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Totals Row */}
        {ov && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="revenue-totals">
            <StatCard icon={DollarSign} label="Gross Revenue" value={fmt(ov.totals.gross_revenue)} sub="/month" color="#6366f1" />
            <StatCard icon={TrendingUp} label="Net Revenue" value={fmt(ov.totals.net_revenue)} sub="after fees" color="#10b981" />
            <StatCard icon={Users} label="Paid Subscribers" value={ov.totals.total_subscribers} sub="across all platforms" color="#f59e0b" />
            <StatCard icon={Store} label="Store Fees" value={fmt(ov.totals.store_fees)} sub="/month" color="#ef4444" />
          </div>
        )}

        {/* Platform Cards */}
        {ov && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="platform-cards">
            {Object.entries(PLATFORM_CONFIG).map(([key, cfg]) => {
              const p = ov.platforms[key] || {};
              return <PlatformCard key={key} config={cfg} data={p} isDark={isDark} colors={colors} />;
            })}
          </div>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Subscriber Distribution Pie */}
          {pieData.length > 0 && (
            <div className="rounded-lg border p-5" style={{ borderColor: colors.cardBorder, background: colors.cardBg }} data-testid="subscriber-pie-chart">
              <h3 className="font-semibold mb-4" style={{ color: colors.text }}>Subscriber Distribution</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={3}>
                    {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${colors.cardBorder}`, borderRadius: 8, color: colors.text }} />
                  <Legend formatter={(v) => <span style={{ color: colors.text, fontSize: 12 }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Trends Bar Chart */}
          {trends?.daily && (
            <div className="rounded-lg border p-5" style={{ borderColor: colors.cardBorder, background: colors.cardBg }} data-testid="trends-bar-chart">
              <h3 className="font-semibold mb-4" style={{ color: colors.text }}>Subscriptions (Last 30 Days)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={trends.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.cardBorder} />
                  <XAxis dataKey="date" tick={{ fill: colors.textMuted, fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fill: colors.textMuted, fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${colors.cardBorder}`, borderRadius: 8, color: colors.text }} />
                  <Bar dataKey="web" fill="#6366f1" stackId="a" radius={[0, 0, 0, 0]} name="Web" />
                  <Bar dataKey="ios" fill="#f59e0b" stackId="a" name="iOS" />
                  <Bar dataKey="android" fill="#10b981" stackId="a" radius={[4, 4, 0, 0]} name="Android" />
                  <Legend formatter={(v) => <span style={{ color: colors.text, fontSize: 12 }}>{v}</span>} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Subscriptions Table */}
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: colors.cardBorder, background: colors.cardBg }} data-testid="subscriptions-table">
          <div className="p-4 flex flex-wrap items-center justify-between gap-3" style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
            <h3 className="font-semibold" style={{ color: colors.text }}>All Subscriptions</h3>
            <div className="flex gap-2">
              <select value={filter.platform} onChange={e => setFilter(f => ({ ...f, platform: e.target.value, page: 1 }))}
                className="text-xs px-2.5 py-1.5 rounded border" data-testid="filter-platform"
                style={{ borderColor: colors.inputBorder, background: colors.input, color: colors.text }}>
                <option value="all">All Platforms</option>
                <option value="web">Web</option>
                <option value="ios">iOS</option>
                <option value="android">Android</option>
              </select>
              <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value, page: 1 }))}
                className="text-xs px-2.5 py-1.5 rounded border" data-testid="filter-status"
                style={{ borderColor: colors.inputBorder, background: colors.input, color: colors.text }}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="canceling">Canceling</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                  {['User', 'Platform', 'Tier', 'Price', 'Net', 'Status', 'Expires'].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 font-medium" style={{ color: colors.textMuted }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subs?.subscriptions?.length > 0 ? subs.subscriptions.map((s, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                    <td className="px-4 py-2.5" style={{ color: colors.text }}>
                      <div className="font-medium text-sm">{s.user_name || s.user_email || s.user_id}</div>
                      {s.user_email && s.user_name && <div className="text-xs" style={{ color: colors.textMuted }}>{s.user_email}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <PlatformBadge platform={s.platform} isDark={isDark} />
                    </td>
                    <td className="px-4 py-2.5 capitalize" style={{ color: colors.text }}>{s.tier}</td>
                    <td className="px-4 py-2.5" style={{ color: colors.text }}>{fmt(s.monthly_price)}</td>
                    <td className="px-4 py-2.5" style={{ color: '#10b981' }}>{fmt(s.net_price)}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={s.status} isDark={isDark} /></td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: colors.textMuted }}>
                      {s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-sm" style={{ color: colors.textMuted }}>No subscriptions found</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {subs && subs.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: `1px solid ${colors.cardBorder}` }}>
              <span className="text-xs" style={{ color: colors.textMuted }}>
                Page {subs.page} of {subs.pages} ({subs.total} total)
              </span>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" disabled={filter.page <= 1} data-testid="prev-page-btn"
                  onClick={() => setFilter(f => ({ ...f, page: f.page - 1 }))} style={{ color: colors.text }}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" disabled={filter.page >= subs.pages} data-testid="next-page-btn"
                  onClick={() => setFilter(f => ({ ...f, page: f.page + 1 }))} style={{ color: colors.text }}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  const { isDark, colors } = useAdminTheme();
  return (
    <div className="rounded-xl p-4" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
        <span className="text-xs font-medium" style={{ color: colors.textMuted }}>{label}</span>
      </div>
      <div className="text-lg font-bold" style={{ color: colors.text }}>{value}</div>
      {sub && <div className="text-xs mt-0.5" style={{ color: colors.textMuted }}>{sub}</div>}
    </div>
  );
}

function PlatformCard({ config, data, isDark, colors }) {
  const { label, icon: Icon, color, cut } = config;
  return (
    <div className="rounded-xl p-5" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}
      data-testid={`platform-card-${label.includes('iOS') ? 'ios' : label.includes('Android') ? 'android' : 'web'}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        <div>
          <div className="font-semibold text-sm" style={{ color: colors.text }}>{label}</div>
          <div className="text-xs" style={{ color: colors.textMuted }}>Store fee: {cut}</div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-xs" style={{ color: colors.textMuted }}>Subscribers</span>
          <span className="text-sm font-semibold" style={{ color: colors.text }}>{data.subscribers || 0}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-xs" style={{ color: colors.textMuted }}>Gross</span>
          <span className="text-sm font-medium" style={{ color: colors.text }}>{fmt(data.gross_revenue)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-xs" style={{ color: colors.textMuted }}>Store Cut</span>
          <span className="text-sm" style={{ color: '#ef4444' }}>-{fmt(data.store_cut)}</span>
        </div>
        <div className="pt-2 flex justify-between" style={{ borderTop: `1px solid ${colors.cardBorder}` }}>
          <span className="text-xs font-medium" style={{ color: colors.textMuted }}>Net Revenue</span>
          <span className="text-sm font-bold" style={{ color: '#10b981' }}>{fmt(data.net_revenue)}</span>
        </div>
        {/* Tier breakdown */}
        {data.tiers && Object.keys(data.tiers).length > 0 && (
          <div className="pt-2 space-y-1" style={{ borderTop: `1px solid ${colors.cardBorder}` }}>
            {Object.entries(data.tiers).map(([tier, count]) => (
              <div key={tier} className="flex justify-between text-xs">
                <span className="capitalize" style={{ color: colors.textMuted }}>{tier}</span>
                <span style={{ color: colors.text }}>{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PlatformBadge({ platform, isDark }) {
  const p = platform?.toLowerCase() || 'web';
  const cfg = p.includes('ios') || p.includes('apple')
    ? { label: 'iOS', bg: isDark ? '#78350f' : '#fef3c7', color: isDark ? '#fbbf24' : '#92400e' }
    : p.includes('android') || p.includes('google')
      ? { label: 'Android', bg: isDark ? '#052e16' : '#dcfce7', color: isDark ? '#4ade80' : '#16a34a' }
      : { label: 'Web', bg: isDark ? '#1e1b4b' : '#e0e7ff', color: isDark ? '#a5b4fc' : '#4338ca' };
  return (
    <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

function StatusBadge({ status, isDark }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.expired;
  return (
    <span className="text-xs font-medium px-2 py-0.5 rounded-full"
      style={{ background: isDark ? s.bg : s.bgLight, color: isDark ? s.text : s.textLight }}>
      {s.label}
    </span>
  );
}
